const INBOX_FILTER_KEY = "landsaas-inbox-filters-v1";
const INBOX_SAVED_VIEWS_KEY = "landsaas-inbox-saved-views-v1";
const ADMIN_REPORTING_KEY = "landsaas-admin-reporting-filters-v1";

function titleCase(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function percent(part, total) {
  if (!total) {
    return "0%";
  }
  return `${Math.round((Number(part || 0) / total) * 100)}%`;
}

function safeParse(value, fallback) {
  if (!value) {
    return fallback;
  }
  try {
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
}

function withDefaults(filters) {
  return {
    search: "",
    status: "open",
    aging_bucket: "",
    assigned_user_id: "",
    date_from: "",
    date_to: "",
    ...((filters || {})),
  };
}

function toConversationDate(value, endOfDay = false) {
  if (!value) {
    return null;
  }
  return `${value}T${endOfDay ? "23:59:59" : "00:00:00"}Z`;
}

function stringMatch(haystack, needle) {
  return String(haystack || "").toLowerCase().includes(String(needle || "").trim().toLowerCase());
}

function conversationActivityAt(item) {
  return item.last_message_at || item.created_at || null;
}

function compareDateRange(item, filters) {
  const activityAt = conversationActivityAt(item);
  if (!activityAt) {
    return true;
  }
  const activityMs = Date.parse(activityAt);
  if (Number.isNaN(activityMs)) {
    return true;
  }
  const fromValue = toConversationDate(filters.date_from, false);
  const toValue = toConversationDate(filters.date_to, true);
  if (fromValue && activityMs < Date.parse(fromValue)) {
    return false;
  }
  if (toValue && activityMs > Date.parse(toValue)) {
    return false;
  }
  return true;
}

export function defaultInboxFilters() {
  return withDefaults({});
}

export function defaultAdminReportingFilters() {
  return {
    interval: "week",
    status: "",
    assigned_user_id: "",
    date_from: "",
    date_to: "",
  };
}

export function loadInboxFilters(storage, surfaceKey) {
  const payload = safeParse(storage?.getItem?.(INBOX_FILTER_KEY), {});
  return withDefaults(payload?.[surfaceKey] || {});
}

export function persistInboxFilters(storage, surfaceKey, filters) {
  if (!storage?.setItem) {
    return;
  }
  const payload = safeParse(storage.getItem(INBOX_FILTER_KEY), {});
  payload[surfaceKey] = withDefaults(filters);
  storage.setItem(INBOX_FILTER_KEY, JSON.stringify(payload));
}

export function loadSavedInboxViews(storage, surfaceKey) {
  const payload = safeParse(storage?.getItem?.(INBOX_SAVED_VIEWS_KEY), {});
  return Array.isArray(payload?.[surfaceKey]) ? payload[surfaceKey] : [];
}

export function saveInboxViewPreset(storage, surfaceKey, presetName, filters) {
  const name = String(presetName || "").trim();
  if (!name || !storage?.setItem) {
    return loadSavedInboxViews(storage, surfaceKey);
  }
  const payload = safeParse(storage.getItem(INBOX_SAVED_VIEWS_KEY), {});
  const current = Array.isArray(payload?.[surfaceKey]) ? payload[surfaceKey] : [];
  const preset = {
    id: `${surfaceKey}-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${Date.now()}`,
    name,
    filters: withDefaults(filters),
  };
  payload[surfaceKey] = [...current.filter((item) => item.name.toLowerCase() !== name.toLowerCase()), preset].slice(-8);
  storage.setItem(INBOX_SAVED_VIEWS_KEY, JSON.stringify(payload));
  return payload[surfaceKey];
}

export function deleteInboxViewPreset(storage, surfaceKey, presetId) {
  if (!storage?.setItem) {
    return loadSavedInboxViews(storage, surfaceKey);
  }
  const payload = safeParse(storage.getItem(INBOX_SAVED_VIEWS_KEY), {});
  const current = Array.isArray(payload?.[surfaceKey]) ? payload[surfaceKey] : [];
  payload[surfaceKey] = current.filter((item) => item.id !== presetId);
  storage.setItem(INBOX_SAVED_VIEWS_KEY, JSON.stringify(payload));
  return payload[surfaceKey];
}

export function loadAdminReportingFilters(storage) {
  return {
    ...defaultAdminReportingFilters(),
    ...(safeParse(storage?.getItem?.(ADMIN_REPORTING_KEY), {}) || {}),
  };
}

export function persistAdminReportingFilters(storage, filters) {
  if (!storage?.setItem) {
    return;
  }
  storage.setItem(ADMIN_REPORTING_KEY, JSON.stringify({ ...defaultAdminReportingFilters(), ...(filters || {}) }));
}

export function buildInboxViewRequest(view, filters) {
  const normalized = withDefaults(filters);
  if (view.key === "open" || view.key === "overdue") {
    const params = new URLSearchParams({ limit: "20", status: normalized.status || "open" });
    if (normalized.search) {
      params.set("search", normalized.search);
    }
    if (normalized.assigned_user_id) {
      params.set("assigned_user_id", normalized.assigned_user_id);
    }
    if (normalized.aging_bucket) {
      params.set("aging_bucket", normalized.aging_bucket);
    }
    if (normalized.date_from) {
      params.set("date_from", toConversationDate(normalized.date_from, false));
    }
    if (normalized.date_to) {
      params.set("date_to", toConversationDate(normalized.date_to, true));
    }
    if (view.key === "overdue") {
      params.set("aging_bucket", "overdue");
      params.set("status", "open");
    }
    return { path: `/api/conversations?${params.toString()}`, localFilter: false };
  }
  return { path: view.path, localFilter: true };
}

export function applyClientConversationFilters(items, filters) {
  const normalized = withDefaults(filters);
  return (items || []).filter((item) => {
    if (normalized.status && item.status !== normalized.status) {
      return false;
    }
    if (normalized.aging_bucket && item.aging_bucket !== normalized.aging_bucket) {
      return false;
    }
    if (normalized.assigned_user_id && item.assigned_user_id !== normalized.assigned_user_id) {
      return false;
    }
    if (normalized.search) {
      const searchValue = normalized.search.trim().toLowerCase();
      const matches =
        stringMatch(item.contact_name, searchValue) ||
        stringMatch(item.display_number, searchValue) ||
        stringMatch(item.phone_e164, searchValue);
      if (!matches) {
        return false;
      }
    }
    return compareDateRange(item, normalized);
  });
}

export function buildAdminReportingQuery(filters) {
  const normalized = { ...defaultAdminReportingFilters(), ...(filters || {}) };
  const params = new URLSearchParams({ interval: normalized.interval || "week" });
  if (normalized.status) {
    params.set("status", normalized.status);
  }
  if (normalized.assigned_user_id) {
    params.set("assigned_user_id", normalized.assigned_user_id);
  }
  if (normalized.date_from) {
    params.set("date_from", toConversationDate(normalized.date_from, false));
  }
  if (normalized.date_to) {
    params.set("date_to", toConversationDate(normalized.date_to, true));
  }
  return `/api/conversations/reports/operational?${params.toString()}`;
}

export function summarizeOperationalReport(report) {
  const totals = report?.totals || {};
  return [
    {
      label: "Inbound volume",
      value: totals.inbound_volume || 0,
      detail: `${report.interval} cadence`,
    },
    {
      label: "Assignment latency",
      value: totals.avg_assignment_latency_seconds ?? "n/a",
      detail: `${totals.assignment_latency_count || 0} measured`,
    },
    {
      label: "Response latency",
      value: totals.avg_first_response_latency_seconds ?? "n/a",
      detail: `${totals.response_latency_count || 0} measured`,
    },
    {
      label: "Disposition total",
      value: totals.disposition_total || 0,
      detail: Object.keys(totals.disposition_counts || {}).length ? "rates available" : "no dispositions",
    },
  ];
}

export function buildOperationalReportWorkspace(report) {
  const totals = report?.totals || {};
  const series = Array.isArray(report?.series) ? report.series : [];
  const sortedOutcomes = Object.entries(totals.disposition_counts || {})
    .sort((left, right) => Number(right[1] || 0) - Number(left[1] || 0));
  const dominantOutcome = sortedOutcomes[0] || null;
  return {
    summaryCards: [
      {
        label: "Reporting window",
        value: titleCase(report?.interval || "week"),
        detail: `${series.length} bucket(s) returned`,
        tone: series.length ? "ready" : "muted",
      },
      {
        label: "Dominant outcome",
        value: dominantOutcome ? titleCase(dominantOutcome[0]) : "No outcomes",
        detail: dominantOutcome ? `${percent(dominantOutcome[1], totals.disposition_total || 0)} of recorded dispositions` : "No disposition mix is available for this window.",
        tone: dominantOutcome ? "ready" : "muted",
      },
      {
        label: "Avg response",
        value: totals.avg_first_response_latency_seconds ?? "n/a",
        detail: `${totals.response_latency_count || 0} response samples`,
        tone: totals.avg_first_response_latency_seconds != null ? "stale" : "muted",
      },
    ],
    insightRows: [
      {
        label: "Inbound posture",
        value: totals.inbound_volume || 0,
        tone: totals.inbound_volume ? "ready" : "muted",
        meta: `Assignment latency measured on ${totals.assignment_latency_count || 0} records.`,
      },
      {
        label: "Assignment pace",
        value: totals.avg_assignment_latency_seconds ?? "n/a",
        tone: totals.avg_assignment_latency_seconds != null ? "stale" : "muted",
        meta: `${totals.assignment_latency_count || 0} assignment samples in this window.`,
      },
      {
        label: "Disposition coverage",
        value: totals.disposition_total || 0,
        tone: totals.disposition_total ? "ready" : "muted",
        meta: sortedOutcomes.length ? `${sortedOutcomes.length} outcome type(s) recorded.` : "No outcomes were recorded for this filter set.",
      },
    ],
    seriesRows: series.map((item) => {
      const metrics = item.metrics || {};
      const topOutcome = Object.entries(metrics.disposition_counts || {})
        .sort((left, right) => Number(right[1] || 0) - Number(left[1] || 0))[0] || null;
      return {
        label: item.period_start,
        value: metrics.avg_first_response_latency_seconds != null ? `${metrics.avg_first_response_latency_seconds}s response` : "n/a response",
        tone: metrics.avg_first_response_latency_seconds != null ? "ready" : "muted",
        meta: `${metrics.inbound_volume || 0} inbound | ${metrics.disposition_total || 0} dispositions${topOutcome ? ` | top outcome ${titleCase(topOutcome[0])}` : ""}`,
      };
    }),
  };
}
